import * as React from 'react';
import { Component, inject, deps, observer } from '../lib/component';
import { classes, style } from 'typestyle';
import { percent, px } from 'csx';
import { centerCenter, horizontal } from 'csstips';
import { Loading } from '../components/loading/loading';
import { ButtonPill } from '../components/button-pill';
import { colorGunmetal, colorWhite } from '../theme/color';
import { mediaMobileOnly, mediaTablet } from '../theme/media';
import { AccountCommunicationPage } from './account-communication';
import { AnalyticsController } from '../modules/analytics/controller';
import PageAccountFavouritesListing from '../components/page-account-favourites-listing';
import { BROWSE_ROUTE, SEARCH_ROUTE } from '../constants';
import { PlaylistTile } from '../components/project-happy/molecules/PlaylistTile';
import FavouritePlaylistButtonCompat from '../components/compat/favourite-playlist-button-compat';
import { TrackLine } from '../components/project-happy/molecules/TrackLine';
import { AccountLayout } from '../components/project-happy/templates/AccountLayout';
import { spacing } from '../theme/spacing';
import { ArtistTile } from '../components/project-happy/molecules/ArtistTile';
import { wideDesktopBreakpoint } from '../components/project-happy/config';
import { MetaTitle } from '../components/project-happy/atoms/MetaTitle';

export interface AccountFavouritesPageProps {
  i18n?: {
    loading: string;
    title: string;
    titleTracks: string;
    titleArtists: string;
    titlePlaylists: string;
  };
  analytics: AnalyticsController;
  onClickLink(event: React.MouseEvent<any>): any;
}

const TRACK_LISTING = 'track-listing';
const ARTIST_LISTING = 'artist-listing';
const PLAYLIST_LISTING = 'playlist-listing';

@inject(deps)
@observer
export class AccountFavouritesPage extends Component<AccountFavouritesPageProps> {
  render() {
    const { i18n = AccountFavouritesPage.i18n, onClickLink } = this.props;
    const model = this.props.model.page.favourites;
    const controller = this.props.controller.page.favourites;
    const { handleClickLink } = this.props.controller.ui;

    const meta = <MetaTitle title="Favorites" />;

    if (model.loading)
      return (
        <AccountLayout>
          {meta}
          <div className={AccountCommunicationPage.styles.containerLoading}>
            <Loading />
          </div>
        </AccountLayout>
      );

    return (
      <AccountLayout wideContainer={true}>
        {meta}
        <h1>Favorites</h1>

        <div
          className={classes(
            AccountFavouritesPage.styles.container,
            model.hasTracks && AccountFavouritesPage.styles.withTracks
          )}
        >
          <PageAccountFavouritesListing
            className={TRACK_LISTING}
            title={i18n.titleTracks}
            isEmpty={!model.hasTracks}
            emptyText="You don't have any favorite tracks yet"
            emptyHref={SEARCH_ROUTE}
            emptyCta="Search tracks"
            onClickLink={handleClickLink}
          >
            {model.infiniteTracks.tracks.map((track, i) => (
              <div key={track.identity} className={AccountFavouritesPage.styles.trackLineWrapper}>
                <TrackLine track={track} onClickLink={onClickLink} />
              </div>
            ))}
            {model.hasMoreTracks && !model.loadingMoreTracks && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <ButtonPill onClick={controller.loadMoreTracks}>Load more</ButtonPill>
              </div>
            )}
            {model.loadingMoreTracks && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <Loading />
              </div>
            )}
          </PageAccountFavouritesListing>
          <PageAccountFavouritesListing
            className={ARTIST_LISTING}
            title={i18n.titleArtists}
            isEmpty={!model.hasArtists}
            emptyText="You don't have any favorite artists yet"
            emptyHref={SEARCH_ROUTE}
            emptyCta="Search artists"
            onClickLink={handleClickLink}
          >
            <div data-test-favourited-artists className={AccountFavouritesPage.styles.grid}>
              {model.filteredArtists.map((artist, index) => (
                <ArtistTile
                  className={AccountFavouritesPage.styles.artistTile}
                  key={index.toString()}
                  artist={artist}
                  onClick={() => handleClickLink}
                />
              ))}
            </div>
            {model.hasMoreArtists && !model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <ButtonPill onClick={controller.loadMoreArtists}>Load more</ButtonPill>
              </div>
            )}
            {model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <Loading />
              </div>
            )}
          </PageAccountFavouritesListing>
          <PageAccountFavouritesListing
            className={PLAYLIST_LISTING}
            title={i18n.titlePlaylists}
            isEmpty={!model.hasPlaylists}
            emptyText="You don't have any favorite playlists yet"
            emptyHref={BROWSE_ROUTE}
            emptyCta="Browse playlists"
            onClickLink={handleClickLink}
          >
            <div data-test-favourited-playlists className={AccountFavouritesPage.styles.grid}>
              {model.filteredPlaylists.map((playlist, index) => (
                <PlaylistTile key={index} className={AccountFavouritesPage.styles.playlistTile} playlist={playlist}>
                  <FavouritePlaylistButtonCompat identity={playlist.uuid} />
                </PlaylistTile>
              ))}
            </div>
            {model.hasMoreArtists && !model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <ButtonPill onClick={controller.loadMorePlaylists}>Load more</ButtonPill>
              </div>
            )}
            {model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <Loading />
              </div>
            )}
          </PageAccountFavouritesListing>
        </div>
      </AccountLayout>
    );
  }

  static i18n = {
    loading: 'Loading...',
    title: 'Favorites',
    titleTracks: 'Favorite tracks',
    titleArtists: 'Favorite artists',
    titlePlaylists: 'Favorite playlists',
  };

  static styles = {
    accountLayoutWrapper: style({
      maxWidth: wideDesktopBreakpoint,
    }),
    container: style(
      {
        display: 'grid',
        gridTemplate: '1fr / auto',
        gap: `${spacing.XLARGE} ${spacing.DEFAULT}`,
      },
      mediaTablet({
        gridTemplate: 'repeat(2, auto) / repeat(3, 1fr)',
      })
    ),
    withTracks: style(
      mediaTablet({
        $nest: {
          [`& > .${TRACK_LISTING}`]: {
            gridArea: '1/1/3/3',
          },
          [`& > .${ARTIST_LISTING}`]: {
            gridArea: '1/3/2/4',
          },
          [`& > .${PLAYLIST_LISTING}`]: {
            gridArea: '2/3/3/4',
          },
        },
      })
    ),

    loading: style({
      height: px(100),
      ...centerCenter,
    }),
    loadMore: style({
      ...centerCenter,
      padding: '20px',
    }),
    grid: style({
      display: 'grid',
      gridTemplate: 'auto / repeat(2, 1fr)',
      width: percent(100),
      gap: spacing.XSMALL,
    }),
    artistTile: style({
      margin: 0,
      width: '100%',
    }),
    playlistTile: style({
      margin: 0,
      width: '100%',
    }),
    trackLineWrapper: style({
      maxWidth: '1000px',
    }),
  };
}
